import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Layout } from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';

const STATUS = {
  pending_verification: ['Pendiente de verificación', 'bg-amber-100 text-amber-800'],
  verified: ['Identidad verificada', 'bg-sky-100 text-sky-800'],
  pending_review: ['En revisión (bloqueos)', 'bg-orange-100 text-orange-800'],
  completed: ['Completada', 'bg-emerald-100 text-emerald-800'],
  rejected: ['Rechazada', 'bg-slate-200 text-slate-700'],
};
const VIA = { email_link: 'Enlace por correo', manual_admin: 'Manual (admin)', authenticated_session: 'Sesión + contraseña' };
const BLOCKERS = {
  active_orders_as_buyer: 'Pedidos activos (comprador)', active_orders_as_seller: 'Ventas activas',
  open_pqr: 'PQR abiertas', seller_balance_pending: 'Saldo/retiros pendientes',
  apple_revocation_pending: 'Revocación Apple pendiente', user_not_found: 'Usuario no encontrado',
};

const NoteDialog = ({ action, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  if (!action) return null;
  const isVerify = action.type === 'verify';
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/account-deletion/requests/${action.id}/${isVerify ? 'verify-manual' : 'reject'}`, { note });
      toast.success(isVerify ? 'Identidad marcada como verificada' : 'Solicitud rechazada');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none" data-testid="deletion-note-dialog">
        <DialogHeader>
          <DialogTitle>{isVerify ? 'Verificación manual de identidad' : 'Rechazar solicitud'}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">
          {isVerify ? 'Solo si el titular respondió desde el correo registrado de la cuenta. Describe la evidencia (fecha, canal).'
            : 'Indica el motivo del rechazo.'}
        </p>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="rounded-none"
                  data-testid="deletion-note-input" />
        <Button onClick={submit} disabled={busy || note.trim().length < 10} data-testid="deletion-note-submit-btn"
                className="rounded-none bg-slate-900 font-bold">{busy ? 'Guardando…' : 'Confirmar'}</Button>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ r, onAction, onExecute }) => {
  const [label, cls] = STATUS[r.status] || [r.status, 'bg-slate-100'];
  const open = !['completed', 'rejected'].includes(r.status);
  const canExecute = ['verified', 'pending_review'].includes(r.status) && !(r.blockers || []).length;
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`deletion-row-${r.id}`}>
      <td className="p-3 text-xs text-slate-500">{formatDate(r.created_at)}</td>
      <td className="p-3 font-mono text-xs" data-testid={`deletion-email-${r.id}`}>{r.email_masked}
        <div className="text-[10px] text-slate-400" data-testid={`deletion-id-${r.id}`}>#{r.id.slice(0, 8)} · {r.user_id}</div></td>
      <td className="p-3 text-xs">{r.role} · {r.source === 'public_form' ? 'Formulario' : 'App/Web'}</td>
      <td className="p-3"><Badge className={`rounded-none ${cls}`} data-testid={`deletion-status-${r.id}`}>{label}</Badge>
        {r.verified_via && <div className="mt-1 text-[11px] text-slate-500">{VIA[r.verified_via] || r.verified_via}</div>}</td>
      <td className="p-3 text-xs text-red-700">{(r.blockers || []).map((b) => BLOCKERS[b] || b).join(', ') || '—'}</td>
      <td className="p-3">
        {open && (
          <div className="flex flex-wrap gap-2">
            {r.status === 'pending_verification' && (
              <Button size="sm" variant="outline" className="rounded-none" data-testid={`deletion-verify-btn-${r.id}`}
                      onClick={() => onAction({ type: 'verify', id: r.id })}>Verificar manualmente</Button>
            )}
            <Button size="sm" disabled={!canExecute} className="rounded-none bg-red-700 hover:bg-red-800"
                    data-testid={`deletion-execute-btn-${r.id}`} onClick={() => onExecute(r)}>Ejecutar</Button>
            <Button size="sm" variant="ghost" className="rounded-none" data-testid={`deletion-reject-btn-${r.id}`}
                    onClick={() => onAction({ type: 'reject', id: r.id })}>Rechazar</Button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default function AdminAccountDeletion() {
  useSEO({ title: 'Eliminación de cuentas | Admin' });
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState([]);
  const [action, setAction] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get('/admin/account-deletion/requests', { params: status === 'all' || status === 'open' ? {} : { status } });
    setRows(status === 'open' ? data.requests.filter((r) => !['completed', 'rejected'].includes(r.status)) : data.requests);
  }, [status]);
  useEffect(() => { load().catch(() => toast.error('No se pudieron cargar las solicitudes')); }, [load]);

  const execute = async (r) => {
    if (!window.confirm(`¿Anonimizar definitivamente la cuenta ${r.email_masked}? Se conservan pedidos, pagos y facturas.`)) return;
    try {
      await api.post(`/admin/account-deletion/requests/${r.id}/execute`);
      toast.success('Cuenta anonimizada');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Error');
    }
    load();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="admin-account-deletion-page">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Eliminación de cuentas</h1>
            <p className="text-xs text-slate-500 mt-1">Correos enmascarados. Solo se ejecutan solicitudes con identidad verificada y sin bloqueos.</p>
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-56 rounded-none" data-testid="deletion-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Abiertas</SelectItem>
              <SelectItem value="all">Todas</SelectItem>
              {Object.entries(STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-6 overflow-x-auto border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="deletion-table">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="p-3">Fecha</th><th className="p-3">Correo</th><th className="p-3">Rol · Origen</th>
                <th className="p-3">Estado</th><th className="p-3">Bloqueos</th><th className="p-3">Acciones</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => <Row key={r.id} r={r} onAction={setAction} onExecute={execute} />)}
              {!rows.length && <tr><td colSpan={6} className="p-6 text-center text-slate-500" data-testid="deletion-empty">Sin solicitudes</td></tr>}
            </tbody>
          </table>
        </div>
        <NoteDialog key={action ? `${action.type}-${action.id}` : 'none'} action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />
      </div>
    </Layout>
  );
}
